import React, { useEffect } from 'react';
import { VStack, Button, Icon, HStack, Box } from '@wegroup/design-system';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { CoverageFormValues, CoverageSchema } from '../../../../../types/Form';
import { useTranslation } from 'react-i18next';
import AddInsuranceModalCoverageFormFields from './AddInsuranceModalCoverageFormFields';
import {
  Coverage,
  InsurancePolicyType,
} from '../../../../../../../types/AdvisoryReport';
import { v4 } from 'uuid';
import { PartyType } from '../../../../../../../../../../types/SMT';
import { RiskType } from '../../../../shared/InsuranceProductSearch';

interface Props {
  onCancel: () => void;
  coverage?: Coverage;
  onSubmit: (values: CoverageFormValues) => void;
  partyType?: PartyType;
  riskId?: string;
  riskType?: RiskType;
  type: InsurancePolicyType;
}

const AddInsuranceModalCoverageForm: React.FC<Props> = ({
  onCancel,
  coverage,
  onSubmit,
  partyType,
  riskType,
  riskId,
  type,
}) => {
  const { t } = useTranslation();
  const scrollToRef = React.useRef<HTMLDivElement>(null);

  const methods = useForm<CoverageFormValues>({
    resolver: yupResolver(CoverageSchema),
    defaultValues: coverage
      ? {
          ...coverage,
          commission_percentage: (coverage.commission_percentage || 0) * 100,
          insurance_tax_percentage:
            coverage.insurance_tax_percentage != null
              ? coverage.insurance_tax_percentage
              : 21,
        }
      : {
          id: v4(),
          risk_object_id: '',
          risk_object: '',
          name: '',
          insurance_product_id: '',
          insurance_company: '',
          premium: undefined,
          commission: undefined,
          commission_percentage: undefined,
          insurance_tax_percentage: 21,
          payment_interval: 'ANNUAL',
          insured_value: undefined,
          deductible: '',
        },
  });

  useEffect(() => {
    scrollToRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSubmit = methods.handleSubmit((values) => {
    if (values.commission_percentage !== undefined) {
      onSubmit({
        ...values,
        insurance_tax_percentage: values.insurance_tax_percentage || 0,
        commission: values.commission || 0,
        commission_percentage: Number(
          (Number(values.commission_percentage || 0) / 100).toFixed(2),
        ),
      });
    } else {
      onSubmit({
        ...values,
        insurance_tax_percentage: values.insurance_tax_percentage || 0,
        commission: values.commission || 0,
      });
    }
  });

  return (
    <Box ref={scrollToRef}>
      <FormProvider {...methods}>
        <VStack spacing="4" align="stretch" pb="6" flexGrow="1">
          <AddInsuranceModalCoverageFormFields
            partyType={partyType}
            riskId={riskId}
            riskType={riskType}
            type={type}
          />
          <HStack
            spacing="3"
            bg="white"
            py="4"
            justify="flex-end"
            position="sticky"
            bottom="-26px"
            borderTop="1px"
            borderColor="gray.50"
            zIndex="1"
          >
            <Button size="sm" variant="ghost" onClick={onCancel}>
              {t('CANCEL')}
            </Button>
            <Button
              variant="secondary"
              leftIcon={
                <Icon
                  name={coverage ? 'GeneralActionsSave' : 'GeneralActionsAdd'}
                  boxSize="4"
                />
              }
              size="sm"
              onClick={handleSubmit}
            >
              {coverage
                ? t('SAVE-CHANGES')
                : t(
                    'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ADD-COVERAGE',
                  )}
            </Button>
          </HStack>
        </VStack>
      </FormProvider>
    </Box>
  );
};

export default AddInsuranceModalCoverageForm;
